// client/src/components/paymentReports/PaymentReports.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip
} from '@mui/material';
import {
  DownloadForOffline as DownloadIcon,
  Print as PrintIcon,
  Description as ReportIcon,
  LocalAtm as PaymentIcon,
  Summarize as SummarizeIcon,
  FormatListBulleted as DetailedIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import paymentReportService from '../../services/paymentReportService';

const PaymentReports = () => {
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [reportType, setReportType] = useState('detailed');
  const [generating, setGenerating] = useState(false);
  
  // Current language
  const lang = i18n.language;
  
  // Load transactions with paid items on component mount
  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoading(true);
        const response = await paymentReportService.getTransactionsWithPaidItems();
        setTransactions(response.data);
        setLoading(false);
      } catch (err) {
        setError(t('errorLoadingTransactions'));
        setLoading(false);
      }
    };
    
    loadTransactions();
  }, [t]);
  
  // Handle transaction selection
  const handleTransactionChange = (e) => {
    setSelectedTransaction(e.target.value);
  };
  
  // Handle report type change
  const handleReportTypeChange = (e, newValue) => {
    if (newValue !== null) {
      setReportType(newValue);
    }
  };
  
  // Generate report
  const handleGenerateReport = async () => {
    if (!selectedTransaction) {
      setError(t('pleaseSelectTransaction'));
      return;
    }
    
    try {
      setGenerating(true);
      setError('');
      await paymentReportService.generatePaymentReport(selectedTransaction, reportType);
      setSuccess(t('reportGeneratedSuccess'));
      setGenerating(false);
    } catch (err) {
      setError(t('errorGeneratingReport'));
      setGenerating(false);
    }
  };
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };
  
  // Get selected transaction object
  const getSelectedTransactionObject = () => {
    return transactions.find(transaction => transaction.id === parseInt(selectedTransaction));
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('paymentReports')}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('generatePaymentReport')}
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('noTransactionsWithPaidItems')}
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('selectTransactionAndReportType')}
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="transaction-label">{t('transaction')}</InputLabel>
                  <Select
                    labelId="transaction-label"
                    value={selectedTransaction}
                    onChange={handleTransactionChange}
                    label={t('transaction')}
                  >
                    <MenuItem value="">{t('selectTransaction')}</MenuItem>
                    {transactions.map((transaction) => (
                      <MenuItem key={transaction.id} value={transaction.id}>
                        {transaction.paymentItem?.name} - {transaction.paymentBylaw?.name}
                        {transaction.paidItemsCount > 0 && (
                          <Chip 
                            size="small" 
                            label={`${transaction.paidItemsCount} ${t('paidItems')}`}
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('reportType')}:
                  </Typography>
                  <ToggleButtonGroup
                    value={reportType}
                    exclusive
                    onChange={handleReportTypeChange}
                    aria-label="report type"
                    fullWidth
                  >
                    <ToggleButton value="detailed" aria-label="detailed">
                      <DetailedIcon sx={{ mr: 1 }} />
                      {t('detailed')}
                      <Tooltip title={t('detailedReportDescription')}>
                        <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="summarized" aria-label="summarized">
                      <SummarizeIcon sx={{ mr: 1 }} />
                      {t('summarized')}
                      <Tooltip title={t('summarizedReportDescription')}>
                        <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
            </Grid>
            
            {selectedTransaction && (
              <Box sx={{ mb: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('selectedTransaction')}
                    </Typography>
                    
                    {getSelectedTransactionObject() && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2">{t('transactionName')}:</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {getSelectedTransactionObject().paymentItem?.name}
                          </Typography>
                          
                          <Typography variant="subtitle2">{t('paymentBylaw')}:</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {getSelectedTransactionObject().paymentBylaw?.name}
                          </Typography>
                          
                          <Typography variant="subtitle2">{t('bylawType')}:</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {t(getSelectedTransactionObject().paymentBylaw?.bylaw_type || 'unknown')}
                            {getSelectedTransactionObject().paymentBylaw?.faculty && (
                              <> - {getSelectedTransactionObject().paymentBylaw.faculty[`name_${lang}`]}</>
                            )}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2">{t('totalValue')}:</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {formatCurrency(getSelectedTransactionObject().total_value)}
                          </Typography>
                          
                          <Typography variant="subtitle2">{t('paidItems')}:</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <Chip 
                              size="small" 
                              label={getSelectedTransactionObject().paidItemsCount}
                              color="success"
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGenerateReport}
                      disabled={generating}
                      startIcon={generating ? <CircularProgress size={24} /> : <DownloadIcon />}
                      fullWidth
                    >
                      {generating ? t('generating') : t('generateAndDownload')}
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            )}
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('reportInformation')}:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('detailedReportTitle')}
                    secondary={t('detailedReportDescription')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('summarizedReportTitle')}
                    secondary={t('summarizedReportDescription')}
                  />
                </ListItem>
              </List>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PaymentReports;